import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Section } from "@/components/ui-kit";
import DateRange from "@/components/DateRange";
import { Download, FileText, FileSpreadsheet } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function buildQuery(params) {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  return qs ? `?${qs}` : "";
}

export default function ReportsPage() {
  const [items, setItems] = useState([]);
  const [range, setRange] = useState({ from: "", to: "" });

  useEffect(() => { api.get("/reports/list").then(({ data }) => setItems(data)); }, []);

  const qs = useMemo(() => buildQuery({ date_from: range.from, date_to: range.to }), [range.from, range.to]);

  const link = (key, fmt) => {
    const base = `${BACKEND}/api/reports/export/${key}?format=${fmt}`;
    const dateParams = qs.startsWith("?") ? "&" + qs.slice(1) : "";
    return base + dateParams;
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Branded PDF & CSV exports · filter by date range" />
      <Section title="Date range" testId="reports-range">
        <DateRange value={range} onChange={setRange} testId="reports-date-range" />
        <div className="text-xs text-muted-foreground mt-3">
          Range applies to all downloads below. Loans / Savings / Customers / Tenants / Subscription filter by record creation date; Repayments filter by due date; Journal-based reports (Trial Balance, Income Statement, Income & Expense Ledger) filter by transaction date. Balance Sheet uses the &ldquo;To&rdquo; date as the as-of date.
        </div>
      </Section>
      <Section title={`Available reports (${items.length})`} testId="reports-list">
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No reports available.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((r) => (
              <div key={r.key} className="card-flat p-5" data-testid={`report-${r.key}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="label">{r.scope}</div>
                    <h3 className="font-display text-lg font-semibold tracking-tight mt-1">{r.name}</h3>
                  </div>
                  <FileText className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div className="flex gap-2">
                  <a className="btn-primary flex-1 justify-center text-xs" target="_blank" rel="noreferrer"
                     href={link(r.key, "pdf")}
                     data-testid={`download-${r.key}-pdf`}>
                    <Download className="w-3.5 h-3.5" /> PDF
                  </a>
                  <a className="btn-ghost flex-1 justify-center text-xs" target="_blank" rel="noreferrer"
                     href={link(r.key, "csv")}
                     data-testid={`download-${r.key}-csv`}>
                    <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
